import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { selectedJobAtom, jobCostDetailCondensedAtom, sideMenuBudgetsAtom, sideMenuCostsAtom } from '../store/company';
import { loadPayRateHistory } from '../db/company';
import { isVoltaGridJob } from '../helpers/voltaGrid';
import usePersistedState from './usePersistedState';
import {
    MIN_HRS, buildRates, companyWithout, jobCrafts, progressionRows, mixRateByYear, repriceAt,
    laborToDate, marginScenarios, baseYearsOf, craftLabel,
} from './payRateMarginData';

// Pay Rates page: how each craft's base wage has moved over the last few years
// (JDE payroll, every job), and what the selected job's gross margin would be
// if its crew had been paid an earlier year's rates. The data is one call,
// readPayRateHistory; the math is payRateMarginData.js. The job's revenue, cost
// and overhead are the side menu's EAC figures for the version that is open.

const cache = new Map();
const DEFAULT_MULTIPLIER = 1.633; // the VoltaGrid Service Order labor multiplier

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hrsFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const money = (n) => (n == null || Number.isNaN(n) ? '—' : usd0.format(n));
const rate = (n) => (n == null ? '—' : usd2.format(n));
const pct = (n, d = 1) => (n == null || !Number.isFinite(n) ? '—' : `${(n * 100).toFixed(d)}%`);
const signedPct = (n, d = 1) => (n == null || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n * 100).toFixed(d)}%`);
const signedMoney = (n) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd0.format(Math.abs(n))}`);
const pts = (n) => (n == null || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)} pts`);
// a loss reads red, a gain green (STRIKE_STYLE section 7); the sign stays, so it reads without colour
const toneOf = (n) => (n == null || Math.abs(n) < 0.005 ? 'var(--foreground)' : n > 0 ? 'var(--success)' : 'var(--destructive)');

const PAGE_CSS = `
.pr-tab { padding: 24px; box-sizing: border-box; }
@media (max-width: 640px) { .pr-tab { padding: 16px; } }
.pr-tab tbody tr:hover { background: color-mix(in srgb, var(--muted) 60%, transparent); }
.pr-tab tr[data-pick] { cursor: pointer; }
.pr-tab tr[aria-selected="true"], .pr-tab tr[aria-selected="true"]:hover { background: var(--muted); }
.pr-tab tr[aria-selected="true"] > td:first-child { box-shadow: inset 3px 0 0 var(--destructive); }
.pr-tab button[aria-pressed="false"]:hover { color: var(--foreground); }
.pr-tab button[data-quiet]:not(:disabled):hover { background: var(--muted) !important; }
.pr-scroll { overflow-x: auto; scrollbar-width: thin; scrollbar-color: var(--border) transparent; }
`;

const card = {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-xs)',
};
const cardHeader = { padding: '14px 16px', borderBottom: '1px solid var(--border)' };
const cardTitle = { fontSize: 15, fontWeight: 600, color: 'var(--foreground)' };
const cardNote = { fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 };
const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 12px', whiteSpace: 'nowrap',
};
const td = { padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 14, verticalAlign: 'top' };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const caption = { fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 };

const Stat = ({ label, value, note, tone }) => (
    <div style={{ ...card, padding: '14px 16px', minWidth: 180, flex: '1 1 180px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: tone || 'var(--foreground)', marginTop: 2 }}>{value}</div>
        {note ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{note}</div> : null}
    </div>
);

const Segmented = ({ label, options, value, onChange }) => (
    <div role="group" aria-label={label} style={{ display: 'inline-flex', gap: 2, padding: 3, background: 'var(--muted)', borderRadius: 'var(--radius)', flexWrap: 'wrap' }}>
        {options.map((o) => {
            const on = o.value === value;
            return (
                <button
                    key={String(o.value)}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onChange(o.value)}
                    style={{
                        height: 26, padding: '0 10px', border: 'none', borderRadius: 'var(--radius-inner)', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'inherit',
                        background: on ? 'var(--primary)' : 'transparent',
                        color: on ? 'var(--primary-foreground)' : 'var(--text-secondary)',
                    }}
                >
                    {o.label}
                </button>
            );
        })}
    </div>
);

const OutlineButton = ({ label, onClick, disabled }) => (
    <button
        type="button"
        data-quiet="true"
        onClick={onClick}
        disabled={disabled}
        style={{
            height: 32, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: 'var(--background)', color: 'var(--foreground)', boxShadow: 'var(--shadow-xs)',
            fontSize: 14, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        }}
    >
        {label}
    </button>
);

const ControlLabel = ({ children }) => (
    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', marginRight: 6 }}>{children}</span>
);

const Banner = ({ tone, children }) => (
    <div role={tone === 'bad' ? 'alert' : undefined} style={{
        background: tone === 'bad' ? 'var(--destructive-tint)' : 'var(--warning-tint)',
        color: tone === 'bad' ? 'var(--destructive-text)' : 'var(--warning-text)',
        border: `1px solid color-mix(in srgb, ${tone === 'bad' ? 'var(--destructive)' : 'var(--warning)'} 25%, transparent)`,
        borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 12, fontSize: 14,
    }}>
        {children}
    </div>
);

const PayRateMargin = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [costRows] = useAtom(jobCostDetailCondensedAtom);
    const [budgets] = useAtom(sideMenuBudgetsAtom);
    const [costs] = useAtom(sideMenuCostsAtom);
    const job = selectedJob ? String(selectedJob).trim() : '';

    const [data, setData] = useState(() => (job && cache.get(job)) || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [base, setBase] = usePersistedState('payRates.base', 0, (v) => Number.isInteger(v));
    const [leaveJobOut, setLeaveJobOut] = usePersistedState('payRates.leaveJobOut', false);
    const [craftScope, setCraftScope] = usePersistedState('payRates.craftScope', 'job', (v) => ['job', 'company'].includes(v));
    const [billing, setBilling] = usePersistedState(`payRates.billing.${job}`, isVoltaGridJob(job) ? 'costPlus' : 'fixed', (v) => ['fixed', 'costPlus'].includes(v));
    const [multiplier, setMultiplier] = usePersistedState(`payRates.multiplier.${job}`, String(DEFAULT_MULTIPLIER));
    const requestId = useRef(0);

    const load = useCallback(async (force) => {
        if (!job) return;
        if (!force && cache.get(job)) { setData(cache.get(job)); setError(''); return; }
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const res = await loadPayRateHistory({ job });
            if (id !== requestId.current) return;
            if (!res || res.error || !Array.isArray(res.company)) {
                setError('The payroll history did not load. Try Refresh.');
                setData(null);
                return;
            }
            const next = { ...res, loadedAt: new Date() };
            cache.set(job, next);
            setData(next);
        } catch (e) {
            if (id === requestId.current) { setError('The payroll history did not load. Try Refresh.'); setData(null); }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [job]);

    useEffect(() => {
        setData((job && cache.get(job)) || null);
        load(false);
    }, [job, load]);

    const ready = data && data.job === job;
    const jobRows = useMemo(() => (ready ? data.jobRows || [] : []), [ready, data]);
    const rates = useMemo(
        () => buildRates(ready ? (leaveJobOut ? companyWithout(data.company, jobRows) : data.company) : []),
        [ready, data, jobRows, leaveJobOut],
    );
    const bases = useMemo(() => baseYearsOf(rates), [rates]);
    // Two years back unless another year was picked; "a couple of years ago".
    const baseYear = bases.includes(base) ? base : (bases.length ? bases[Math.max(0, bases.length - 2)] : null);
    const crafts = data?.crafts || {};
    const jc = useMemo(() => jobCrafts(jobRows), [jobRows]);
    const jobByCraft = useMemo(() => new Map(jc.map((x) => [x.craft, x])), [jc]);

    const progCodes = useMemo(() => {
        if (craftScope === 'job') return jc.map((x) => x.craft);
        // The 25 crafts with the most regular hours across the company in the latest two years.
        const ys = rates.years.slice(-2);
        const hrs = new Map();
        (ready ? data.company : []).forEach((r) => {
            if (!ys.includes(r.yr)) return;
            hrs.set(r.craft, (hrs.get(r.craft) || 0) + r.hrs);
        });
        return [...hrs.entries()].filter(([c]) => c).sort((a, b) => b[1] - a[1]).slice(0, 25).map(([c]) => c);
    }, [craftScope, jc, rates, ready, data]);
    const progression = useMemo(() => progressionRows(rates, progCodes, jobByCraft), [rates, progCodes, jobByCraft]);
    const mix = useMemo(() => mixRateByYear(rates, jc), [rates, jc]);

    const reprices = useMemo(() => bases.map((b) => repriceAt(rates, jobRows, b)), [bases, rates, jobRows]);
    const labor = useMemo(() => laborToDate(costRows), [costRows]);
    const mult = billing === 'costPlus' ? Math.max(0, Number(multiplier) || 0) : 0;
    const totals = useMemo(() => {
        const revenue = (budgets?.contractValuesRevised || 0) + (budgets?.changeOrderRevenue || 0);
        const totalCost = (costs?.labor || 0) + (costs?.equipment || 0) + (costs?.materials || 0) + (costs?.subs || 0) + (costs?.other || 0) + (costs?.contingency || 0);
        return { revenue, totalCost, overhead: costs?.overhead || 0, laborEac: costs?.labor || 0, billMultiplier: mult };
    }, [budgets, costs, mult]);
    const margin = useMemo(() => marginScenarios(totals, labor, reprices), [totals, labor, reprices]);
    const picked = margin.scenarios.find((s) => s.base === baseYear) || null;
    const pickedReprice = reprices.find((p) => p.base === baseYear) || null;

    const wagesPaid = jc.reduce((s, x) => s + x.gross, 0);
    const hoursWorked = jc.reduce((s, x) => s + x.hrs, 0);
    const hasTotals = totals.revenue > 0;
    const lastYear = rates.last?.yr;

    const exportXlsx = () => {
        const wb = XLSX.utils.book_new();
        const rateRows = progression.map((p) => {
            const o = { Craft: craftLabel(crafts, p.craft), Code: p.craft };
            p.byYear.forEach((y) => { o[y.yr === lastYear ? `${y.yr} YTD` : String(y.yr)] = y.rate == null ? '' : Math.round(y.rate * 100) / 100; });
            o[`Change ${p.firstYr ?? ''}-${p.lastYr ?? ''}`] = p.change == null ? '' : Math.round(p.change * 1000) / 10;
            o['Per year %'] = p.perYear == null ? '' : Math.round(p.perYear * 1000) / 10;
            o['This job rate'] = p.jobRate == null ? '' : Math.round(p.jobRate * 100) / 100;
            o['This job hours'] = Math.round(p.jobHrs);
            return o;
        });
        const mixRow = { Craft: "This job's crew mix", Code: '' };
        mix.byYear.forEach((y) => { mixRow[y.yr === lastYear ? `${y.yr} YTD` : String(y.yr)] = y.rate == null ? '' : Math.round(y.rate * 100) / 100; });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rateRows.concat(craftScope === 'job' ? [mixRow] : [])), 'Base rate by craft');
        const marginRows = [{ Rates: 'Actual', 'Wages vs actual %': 100, 'Labor saved to date': 0, 'Labor saved still to come': 0, 'Revenue change': 0, Revenue: Math.round(margin.actual.revenue), GM: Math.round(margin.actual.gm), 'GM %': margin.actual.gmPct == null ? '' : Math.round(margin.actual.gmPct * 1000) / 10, 'Change pts': 0 }]
            .concat([...margin.scenarios].reverse().map((s) => ({
                Rates: `${s.base} rates`, 'Wages vs actual %': Math.round(s.ratio * 1000) / 10, 'Labor saved to date': Math.round(s.jtdSaving),
                'Labor saved still to come': Math.round(s.etcSaving), 'Revenue change': Math.round(s.revenueChange), Revenue: Math.round(s.revenue),
                GM: Math.round(s.gm), 'GM %': s.gmPct == null ? '' : Math.round(s.gmPct * 1000) / 10, 'Change pts': s.ptsChange == null ? '' : Math.round(s.ptsChange * 10) / 10,
            })));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(marginRows), 'Margin');
        if (pickedReprice) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pickedReprice.byCraft.map((x) => ({
                Craft: craftLabel(crafts, x.craft), Code: x.craft, Hours: Math.round(jobByCraft.get(x.craft)?.hrs || 0),
                'Wages paid': Math.round(x.gross * 100) / 100, [`At ${baseYear} rates`]: Math.round(x.atBase * 100) / 100,
                Difference: Math.round((x.gross - x.atBase) * 100) / 100, 'Rate change from': x.src === 'craft' ? 'This craft' : 'Whole company',
            }))), `Job wages at ${baseYear}`);
        }
        XLSX.writeFile(wb, `Pay_Rates_Margin_${job}_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    if (!job) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ ...card, padding: 20, color: 'var(--text-secondary)' }}>Pick a job in the top bar to see its pay rates and margin.</div>
            </div>
        );
    }

    const yearHead = (y) => (y === lastYear ? `${y} YTD` : String(y));

    return (
        <div className="pr-tab">
            <style>{PAGE_CSS}</style>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: '1 1 320px' }}>
                    <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.15, borderLeft: '3px solid var(--destructive)', paddingLeft: 10, color: 'var(--foreground)' }}>
                        Pay rates and margin
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                        Job {job} · JDE payroll since {data?.from ? moment(data.from).format('M/D/YYYY') : '…'}
                        {data?.loadedAt ? ` · loaded ${moment(data.loadedAt).format('h:mm A')}` : ''}
                    </div>
                </div>
                <OutlineButton label="Export to Excel" onClick={exportXlsx} disabled={!ready} />
                <OutlineButton label={loading ? 'Loading…' : 'Refresh'} onClick={() => load(true)} disabled={loading} />
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <ControlLabel>Compare with</ControlLabel>
                    <Segmented label="Compare with" options={bases.map((b) => ({ value: b, label: `${b} rates` }))} value={baseYear} onChange={setBase} />
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <ControlLabel>Company rates from</ControlLabel>
                    <Segmented
                        label="Company rates from"
                        options={[{ value: false, label: 'Every job' }, { value: true, label: 'Other jobs only' }]}
                        value={leaveJobOut}
                        onChange={setLeaveJobOut}
                    />
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ControlLabel>Labor bills</ControlLabel>
                    <Segmented
                        label="Labor bills"
                        options={[{ value: 'fixed', label: 'Fixed price or rate sheet' }, { value: 'costPlus', label: 'At wage × multiplier' }]}
                        value={billing}
                        onChange={setBilling}
                    />
                    {billing === 'costPlus' ? (
                        <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={multiplier}
                            onChange={(e) => setMultiplier(e.target.value)}
                            aria-label="Labor multiplier"
                            style={{
                                height: 32, width: 84, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--input)',
                                background: 'var(--background)', color: 'var(--foreground)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        />
                    ) : null}
                </span>
            </div>

            {error ? <Banner tone="bad">{error}</Banner> : null}
            {ready && !jobRows.length ? <Banner>JDE has no payroll on job {job} yet, so there is no labor to re-price. The rate table below is company-wide.</Banner> : null}
            {ready && jobRows.length && !hasTotals ? <Banner>The job&apos;s revenue and cost totals have not loaded yet, so margin is not shown. They come from the side menu&apos;s budget and EAC.</Banner> : null}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <Stat
                    label="Wages paid to date"
                    value={ready ? money(wagesPaid) : '—'}
                    note={ready && jobRows.length ? `${hrsFmt.format(hoursWorked)} hrs · ${moment(data.jobFirst).format('M/D/YY')} to ${moment(data.jobLast).format('M/D/YY')}` : 'Regular and overtime'}
                />
                <Stat
                    label={baseYear ? `The same work at ${baseYear} rates` : 'At earlier rates'}
                    value={ready && pickedReprice ? money(pickedReprice.atBase) : '—'}
                    note={pickedReprice && pickedReprice.actual ? `${signedPct(pickedReprice.ratio - 1)} against what was paid` : null}
                />
                <Stat
                    label="GM at today's rates"
                    value={hasTotals ? pct(margin.actual.gmPct) : '—'}
                    note={hasTotals ? `${money(margin.actual.gm)} on ${money(margin.actual.revenue)} revenue` : 'EAC, from the side menu'}
                />
                <Stat
                    label={baseYear ? `GM at ${baseYear} rates` : 'GM at earlier rates'}
                    value={hasTotals && picked ? pct(picked.gmPct) : '—'}
                    tone={hasTotals && picked ? toneOf(picked.ptsChange) : undefined}
                    note={hasTotals && picked ? `${pts(picked.ptsChange)} · ${signedMoney(picked.gm - margin.actual.gm)}` : null}
                />
            </div>

            {/* Margin at each earlier year's rates */}
            <div style={{ ...card, marginBottom: 16, overflow: 'hidden' }}>
                <div style={cardHeader}>
                    <div style={cardTitle}>Gross margin at earlier pay rates</div>
                    <div style={cardNote}>
                        EAC margin if this job&apos;s crew were paid each year&apos;s rates, to date and for the labor still to come. Pick a row to see it by craft.
                    </div>
                </div>
                <div className="pr-scroll">
                    <table aria-label="Gross margin at earlier pay rates" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={th}>Pay rates</th>
                                <th style={{ ...th, ...num }}>Wages vs paid</th>
                                <th style={{ ...th, ...num }}>Labor saved to date</th>
                                <th style={{ ...th, ...num }}>Labor saved still to come</th>
                                {mult ? <th style={{ ...th, ...num }}>Revenue change</th> : null}
                                <th style={{ ...th, ...num }}>Gross margin</th>
                                <th style={{ ...th, ...num }}>GM %</th>
                                <th style={{ ...th, ...num }}>Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={td}>
                                    <div style={{ fontWeight: 500 }}>What was paid</div>
                                    <div style={caption}>Today&apos;s EAC</div>
                                </td>
                                <td style={{ ...td, ...num }}>100.0%</td>
                                <td style={{ ...td, ...num }}>—</td>
                                <td style={{ ...td, ...num }}>
                                    {hasTotals ? '—' : ''}
                                    {hasTotals && margin.actual.remaining ? <div style={caption}>{money(margin.actual.remaining)} labor left</div> : null}
                                </td>
                                {mult ? <td style={{ ...td, ...num }}>—</td> : null}
                                <td style={{ ...td, ...num }}>{hasTotals ? money(margin.actual.gm) : '—'}</td>
                                <td style={{ ...td, ...num }}>{hasTotals ? pct(margin.actual.gmPct) : '—'}</td>
                                <td style={{ ...td, ...num }}>—</td>
                            </tr>
                            {[...margin.scenarios].reverse().map((s) => (
                                <tr
                                    key={s.base}
                                    data-pick="true"
                                    aria-selected={s.base === baseYear}
                                    tabIndex={0}
                                    onClick={() => setBase(s.base)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBase(s.base); } }}
                                >
                                    <td style={td}>
                                        <div style={{ fontWeight: 500 }}>{s.base} rates</div>
                                        {s.fallbackShare > 0.05 ? <div style={caption}>{pct(s.fallbackShare, 0)} of wages on the company-wide change</div> : null}
                                    </td>
                                    <td style={{ ...td, ...num }}>{ready && jobRows.length ? pct(s.ratio) : '—'}</td>
                                    <td style={{ ...td, ...num }}>{hasTotals ? signedMoney(s.jtdSaving) : '—'}</td>
                                    <td style={{ ...td, ...num }}>{hasTotals ? signedMoney(s.etcSaving) : '—'}</td>
                                    {mult ? <td style={{ ...td, ...num }}>{hasTotals ? signedMoney(s.revenueChange) : '—'}</td> : null}
                                    <td style={{ ...td, ...num }}>{hasTotals ? money(s.gm) : '—'}</td>
                                    <td style={{ ...td, ...num }}>{hasTotals ? pct(s.gmPct) : '—'}</td>
                                    <td style={{ ...td, ...num, color: hasTotals ? toneOf(s.ptsChange) : undefined }}>{hasTotals ? pts(s.ptsChange) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {hasTotals ? (
                    <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>
                        Labor to date: wages {money(labor.wages)} and payroll tax and fringe {money(labor.fringe)} ({pct(labor.fringeRate)} of wages), which moves with them.
                        {' '}Per diem, truck and rig pay do not change. Still to come: {money(margin.actual.remainingWage)} of wage and fringe in the labor EAC, priced at the latest quarter&apos;s rates.
                        {mult ? ` Cost plus: labor bills at the wage × ${mult}, so lower wages also bill less.` : ''}
                    </div>
                ) : null}
            </div>

            {/* Base rate by craft, year by year */}
            <div style={{ ...card, marginBottom: 16, overflow: 'hidden' }}>
                <div style={{ ...cardHeader, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 300px' }}>
                        <div style={cardTitle}>Base pay rate by craft</div>
                        <div style={cardNote}>
                            Hours-weighted regular-time rate across {leaveJobOut ? 'every other job' : 'every job'} in JDE, by year. A year with under {MIN_HRS} hours for a craft shows no rate.
                        </div>
                    </div>
                    <Segmented
                        label="Crafts shown"
                        options={[{ value: 'job', label: 'Crafts on this job' }, { value: 'company', label: 'Top 25 company-wide' }]}
                        value={craftScope}
                        onChange={setCraftScope}
                    />
                </div>
                <div className="pr-scroll">
                    <table aria-label="Base pay rate by craft" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={th}>Craft</th>
                                {rates.years.map((y) => <th key={y} style={{ ...th, ...num }}>{yearHead(y)}</th>)}
                                <th style={{ ...th, ...num }}>Change</th>
                                <th style={{ ...th, ...num }}>Per year</th>
                                <th style={{ ...th, ...num }}>This job</th>
                                <th style={{ ...th, ...num }}>Job hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!ready ? (
                                <tr><td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={rates.years.length + 5}>{loading ? 'Loading payroll history…' : 'No data.'}</td></tr>
                            ) : null}
                            {ready && !progression.length ? (
                                <tr><td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={rates.years.length + 5}>No crafts to show.</td></tr>
                            ) : null}
                            {progression.map((p) => (
                                <tr key={p.craft || 'none'}>
                                    <td style={{ ...td, minWidth: 180 }}>
                                        <div>{craftLabel(crafts, p.craft)}</div>
                                        {p.craft ? <div style={caption}>Job type {p.craft}</div> : null}
                                    </td>
                                    {p.byYear.map((y, i) => {
                                        const prev = p.byYear.slice(0, i).reverse().find((x) => x.rate != null);
                                        const yoy = y.rate != null && prev ? y.rate / prev.rate - 1 : null;
                                        return (
                                            <td key={y.yr} style={{ ...td, ...num }} title={`${hrsFmt.format(y.hrs)} regular hours`}>
                                                <div>{rate(y.rate)}</div>
                                                {yoy != null ? <div style={caption}>{signedPct(yoy)}</div> : null}
                                            </td>
                                        );
                                    })}
                                    <td style={{ ...td, ...num }}>
                                        <div>{signedPct(p.change)}</div>
                                        {p.change != null ? <div style={caption}>{p.firstYr} to {p.lastYr}</div> : null}
                                    </td>
                                    <td style={{ ...td, ...num }}>{signedPct(p.perYear)}</td>
                                    <td style={{ ...td, ...num }}>{rate(p.jobRate)}</td>
                                    <td style={{ ...td, ...num }}>{p.jobHrs ? hrsFmt.format(p.jobHrs) : '—'}</td>
                                </tr>
                            ))}
                            {ready && craftScope === 'job' && jc.length ? (() => {
                                const ys = mix.byYear.filter((y) => y.rate != null);
                                const f = ys[0]; const l = ys[ys.length - 1];
                                const span = f && l ? l.yr - f.yr : 0;
                                return (
                                    <tr style={{ background: 'var(--subtle)' }}>
                                        <td style={{ ...td, fontWeight: 600 }}>
                                            <div>This job&apos;s crew mix</div>
                                            <div style={{ ...caption, fontWeight: 400 }}>
                                                Company rates weighted by this job&apos;s hours{mix.coverage < 0.995 ? `, ${pct(mix.coverage, 0)} of hours (crafts rated every year)` : ''}
                                            </div>
                                        </td>
                                        {mix.byYear.map((y, i) => {
                                            const prev = i > 0 ? mix.byYear[i - 1] : null;
                                            const yoy = y.rate != null && prev?.rate ? y.rate / prev.rate - 1 : null;
                                            return (
                                                <td key={y.yr} style={{ ...td, ...num, fontWeight: 600 }}>
                                                    <div>{rate(y.rate)}</div>
                                                    {yoy != null ? <div style={{ ...caption, fontWeight: 400 }}>{signedPct(yoy)}</div> : null}
                                                </td>
                                            );
                                        })}
                                        <td style={{ ...td, ...num, fontWeight: 600 }}>{span > 0 ? signedPct(l.rate / f.rate - 1) : '—'}</td>
                                        <td style={{ ...td, ...num, fontWeight: 600 }}>{span > 0 ? signedPct((l.rate / f.rate) ** (1 / span) - 1) : '—'}</td>
                                        <td style={{ ...td, ...num, fontWeight: 600 }}>{hoursWorked ? rate(jc.reduce((s, x) => s + x.basePay, 0) / Math.max(1, jc.reduce((s, x) => s + x.baseHrs, 0))) : '—'}</td>
                                        <td style={{ ...td, ...num, fontWeight: 600 }}>{hrsFmt.format(hoursWorked)}</td>
                                    </tr>
                                );
                            })() : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* The job's wages by craft at the picked year */}
            {ready && pickedReprice && pickedReprice.byCraft.length ? (
                <div style={{ ...card, marginBottom: 16, overflow: 'hidden' }}>
                    <div style={cardHeader}>
                        <div style={cardTitle}>This job&apos;s wages at {baseYear} rates, by craft</div>
                        <div style={cardNote}>Each quarter&apos;s wages scaled by how far that craft&apos;s company rate moved from {baseYear} to the quarter the hours were worked.</div>
                    </div>
                    <div className="pr-scroll">
                        <table aria-label="Job wages by craft" style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Craft</th>
                                    <th style={{ ...th, ...num }}>Hours</th>
                                    <th style={{ ...th, ...num }}>Wages paid</th>
                                    <th style={{ ...th, ...num }}>At {baseYear} rates</th>
                                    <th style={{ ...th, ...num }}>Difference</th>
                                    <th style={th}>Rate change from</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pickedReprice.byCraft.map((x) => (
                                    <tr key={x.craft || 'none'}>
                                        <td style={{ ...td, minWidth: 180 }}>{craftLabel(crafts, x.craft)}</td>
                                        <td style={{ ...td, ...num }}>{hrsFmt.format(jobByCraft.get(x.craft)?.hrs || 0)}</td>
                                        <td style={{ ...td, ...num }}>{money(x.gross)}</td>
                                        <td style={{ ...td, ...num }}>{money(x.atBase)}</td>
                                        <td style={{ ...td, ...num }}>{signedMoney(x.gross - x.atBase)}</td>
                                        <td style={{ ...td, color: 'var(--text-secondary)' }}>{x.src === 'craft' ? 'This craft' : 'Whole company (too few hours to rate the craft)'}</td>
                                    </tr>
                                ))}
                                <tr style={{ background: 'var(--subtle)' }}>
                                    <td style={{ ...td, fontWeight: 600 }}>Total</td>
                                    <td style={{ ...td, ...num, fontWeight: 600 }}>{hrsFmt.format(hoursWorked)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600 }}>{money(pickedReprice.actual)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600 }}>{money(pickedReprice.atBase)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600 }}>{signedMoney(pickedReprice.actual - pickedReprice.atBase)}</td>
                                    <td style={td} />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            <div style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 880, lineHeight: 1.5 }}>
                How this is worked out: rates are JDE timecard history (F0618), regular time only, every job and overhead unit; craft is the JDE job type.
                The job keeps its own crew and its own pay above or below the company average; only the change in each craft&apos;s rate since the year picked is taken out.
                Overtime is 1.5 × base, so it moves the same way. Revenue, cost and overhead are the side menu&apos;s EAC for the version that is open; overhead is held as it is.
            </div>
        </div>
    );
};

export default PayRateMargin;
